import { MetaLayout } from "@/common/MetaLayout";
import {
  DEFAULT_EVENT_QUERY_FILTERS,
  schema,
} from "@/components/EventFilters/schema";
import { EventsCarousel } from "@/components/EventsCarousel";
import { EventsEmpty } from "@/components/EventsEmpty";
import { SECTIONS } from "@/components/EventsSection";
import { TrackOfTheMonth } from "@/components/TrackOfTheMonth";
import { EyeIcon, EyeOffIcon } from "@/icons";
import { Button } from "@/kit/ui/button";
import { EVENT_ORDER } from "@/utils/eventOrder";
import { useRefetchOnParamsChange } from "@/utils/hooks";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import { parseRequestSearch } from "@/utils/validation";
import { Fragment, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Environment, graphql, useRefetchableFragment } from "react-relay";
import { EventsPageQuery as EventsPageQueryType } from "./__generated__/EventsPageQuery.graphql";
import { EventsPageSectionsFragment$key } from "./__generated__/EventsPageSectionsFragment.graphql";
import { GETTING_STARTED_TAG } from "./_app._challenge._layout";
import { EventSectionIcon } from "@/components/EventSectionIcon";

const TUTORIAL_HIDDEN_KEY = "aqora_tutorial_section_hidden" as const;

function isTutorialHidden(): boolean {
  try {
    return localStorage.getItem(TUTORIAL_HIDDEN_KEY) === "true";
  } catch {
    return false;
  }
}

function setTutorialHidden(hidden: boolean): void {
  try {
    if (hidden) {
      localStorage.setItem(TUTORIAL_HIDDEN_KEY, "true");
    } else {
      localStorage.removeItem(TUTORIAL_HIDDEN_KEY);
    }
  } catch {
    // ignored
  }
}

const EventsPageSectionsFragment = graphql`
  fragment EventsPageSectionsFragment on Query
  @refetchable(queryName: "EventsPageSectionsFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    tutorialFilters: { type: "EventQueryFilters" }
    upcomingFilters: { type: "EventQueryFilters" }
    activeFilters: { type: "EventQueryFilters" }
    closedFilters: { type: "EventQueryFilters" }
  ) {
    tutorial: events(after: $cursor, first: 3, filters: $tutorialFilters) {
      totalCount
      ...EventsCarouselFragment
    }
    upcoming: events(after: $cursor, first: $count, filters: $upcomingFilters) {
      totalCount
      ...EventsCarouselFragment
    }
    active: events(after: $cursor, first: $count, filters: $activeFilters) {
      totalCount
      ...EventsCarouselFragment
    }
    closed: events(after: $cursor, first: $count, filters: $closedFilters) {
      totalCount
      ...EventsCarouselFragment
    }
    ...TrackOfTheMonthFragment
  }
`;

export default function EventsPage() {
  const intl = useIntl();
  const { query } = useEventsPageQuery();
  return (
    <MetaLayout
      metaTitle={intl.formatMessage({
        defaultMessage: "Quantum Computing Challenges on Aqora.io | Join now",
      })}
      metaDescription={intl.formatMessage({
        defaultMessage:
          "Participate in quantum computing hackathons at Aqora.io. Demonstrate your skills, collaborate with experts, and push the boundaries of innovation.",
      })}
      hideTitleTemplate
    >
      <EventsSections query={query} />
    </MetaLayout>
  );
}

interface EventsSectionsProps {
  query: EventsPageSectionsFragment$key;
}

function EventsSections(props: EventsSectionsProps) {
  const [data, refetch] = useRefetchableFragment(
    EventsPageSectionsFragment,
    props.query,
  );
  const [tutorialHidden, setTutorialHiddenState] = useState(isTutorialHidden);
  const [hasFilters, setSearchParams] = useRefetchOnParamsChange({
    schema,
    refetch,
    defaultValues: DEFAULT_EVENT_QUERY_FILTERS,
    map: ({ location, sort, ...rest }) => {
      const { order, direction: sortDirection } = EVENT_ORDER.values[sort];
      const base = {
        order,
        sortDirection,
        locations: location ? [location] : undefined,
        ...rest,
      };

      return {
        tutorialFilters: {
          ...base,
          tags: [GETTING_STARTED_TAG, ...(base.tags ?? [])],
        },
        upcomingFilters: {
          ...base,
          status: "UPCOMING",
        },
        activeFilters: {
          ...base,
          status: "ACTIVE",
        },
        closedFilters: {
          ...base,
          status: "CLOSED",
        },
      };
    },
  });
  if (
    data.closed.totalCount === 0 &&
    data.active.totalCount === 0 &&
    data.tutorial.totalCount === 0 &&
    data.upcoming.totalCount === 0
  ) {
    return (
      <EventsEmpty
        hasFilters={hasFilters}
        createEventPath="/challenges/new"
        onClick={() => setSearchParams(DEFAULT_EVENT_QUERY_FILTERS)}
      />
    );
  }
  const toggleTutorial = () => {
    const next = !tutorialHidden;
    setTutorialHidden(next);
    setTutorialHiddenState(next);
  };
  return (
    <div className="pb-10 flex flex-col gap-10">
      {SECTIONS.map(({ key, to, label, variant, Icon }) => (
        <Fragment key={key}>
          <EventsCarousel
            to={to}
            events={data[key]}
            label={label}
            hidden={key === "tutorial" && tutorialHidden}
            description={
              key === "tutorial" && (
                <FormattedMessage defaultMessage="Discover how to compete on the Aqora platform with these tutorials." />
              )
            }
            action={
              key === "tutorial" && (
                <Button size="md" variant="secondary" onClick={toggleTutorial}>
                  {tutorialHidden ? <EyeIcon /> : <EyeOffIcon />}
                  {tutorialHidden ? (
                    <FormattedMessage defaultMessage="Show for me" />
                  ) : (
                    <FormattedMessage defaultMessage="Hide for me" />
                  )}
                </Button>
              )
            }
            icon={<EventSectionIcon Icon={Icon} variant={variant} />}
          />
          {key === "active" && <TrackOfTheMonth query={data} />}
        </Fragment>
      ))}
    </div>
  );
}
const EventsPageQuery = graphql`
  query EventsPageQuery(
    $tutorialFilters: EventQueryFilters
    $upcomingFilters: EventQueryFilters
    $activeFilters: EventQueryFilters
    $closedFilters: EventQueryFilters
  ) {
    ...EventsPageSectionsFragment
      @arguments(
        tutorialFilters: $tutorialFilters
        upcomingFilters: $upcomingFilters
        activeFilters: $activeFilters
        closedFilters: $closedFilters
      )
  }
`;
export const loader =
  (environment: Environment) =>
  ({ request }: LoaderArgs) => {
    const { tags, location, sort, ...filters } = parseRequestSearch(
      schema,
      request,
    );
    const { order, direction: sortDirection } = EVENT_ORDER.values[sort];
    const base = {
      locations: location ? [location] : undefined,
      sortDirection,
      order,
      ...filters,
    };
    return preload<EventsPageQueryType>(environment, EventsPageQuery, {
      tutorialFilters: {
        tags: [GETTING_STARTED_TAG, ...(tags ?? [])],
        ...base,
      },
      upcomingFilters: {
        status: "UPCOMING",
        ...base,
      },
      activeFilters: {
        status: "ACTIVE",
        ...base,
      },
      closedFilters: {
        status: "CLOSED",
        ...base,
      },
    });
  };
const useEventsPageQuery = () => usePreloaded<EventsPageQueryType>();
